import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../../features/auth/services/auth.service';
import { AuthInitializerService } from '../../features/auth/services/auth-initializer.service';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserStateService } from '../services/user-state.service';

/**
 * Guard that protects routes requiring either admin or organization user access
 * Redirects to unauthorized page if not admin or org user
 */
export const orgUserGuard: CanActivateFn = (route, state) => {
  const showDebugInfo = environment.showDebugInfo;
  
  if (showDebugInfo) {
    console.log('Org User guard activated for route:', state.url);
  }
  
  const router = inject(Router);
  const authService = inject(AuthService);
  const userStateService = inject(UserStateService);
  const authInitializer = inject(AuthInitializerService);
  
  // Check if auth is ready
  if (!authInitializer.isAuthReady()) {
    if (showDebugInfo) {
      console.log('Org User guard: Auth not ready, waiting for initialization');
    }
    // Wait for auth to be ready
    return of(authInitializer.initializeAuth()).pipe(
      switchMap(() => checkOrgUserAccess(authService, userStateService, router, showDebugInfo))
    );
  }
  
  return checkOrgUserAccess(authService, userStateService, router, showDebugInfo);
};

/**
 * Helper function to check if user is either admin or has an organization ID
 */
function checkOrgUserAccess(
  authService: AuthService, 
  userStateService: UserStateService, 
  router: Router, 
  showDebugInfo: boolean
): Observable<boolean> {
  // First check if the user is authenticated
  return authService.checkAuthenticated().pipe(
    take(1),
    switchMap(isAuthenticated => {
      if (!isAuthenticated) {
        if (showDebugInfo) {
          console.log('Org User guard: User not authenticated, redirecting to login');
        }
        router.navigate(['/login']);
        return of(false);
      }
      
      // Then check if the user is an admin or has an organization ID
      return userStateService.state$.pipe(
        take(1),
        map(state => {
          // Check if user is admin or has an organization ID
          const hasAccess = state.isAdmin || !!state.organisationId;
          
          if (showDebugInfo) {
            console.log('Org User guard check result:', {
              isAdmin: state.isAdmin,
              organisationId: state.organisationId,
              hasAccess
            });
          }
          
          if (hasAccess) {
            if (showDebugInfo) {
              console.log('Org User guard: Access granted - user is admin or has organization');
            }
            return true;
          }
          
          if (showDebugInfo) {
            console.log('Org User guard: Access denied - user is not admin and has no organization');
          }
          router.navigate(['/unauthorized']);
          return false;
        })
      );
    })
  );
} 